import React from "react";
import { ArrowUpRight, Users, Building2, Cpu } from "lucide-react";
import TiltCard from "@/components/TiltCard";

const items = [
  {
    icon: Users,
    title: "University Workshop",
    bn: "On-campus, hands-on",
    desc: "We come to your campus, train students on practical AI workflows, and leave them with skills they can use in careers and research.",
    cta: "Book Campus Visit",
  },
  {
    icon: Building2,
    title: "Office Training",
    bn: "Upgrade in 30 days",
    desc: "Your team spends hours on tasks AI can do in minutes. Onsite training to upgrade your team's AI output within 30 days.",
    cta: "Train My Team",
  },
  {
    icon: Cpu,
    title: "AI Business Automation",
    bn: "Workflows that run themselves",
    desc: "Custom AI automation, SaaS, and CRMs designed around your operations. Manual work runs itself.",
    cta: "Get a Proposal",
  },
];

export default function Workshops() {
  return (
    <div className="min-h-screen pt-32 relative overflow-hidden">
      <div className="orb orb-violet w-[30rem] h-[30rem] -top-40 -left-40" aria-hidden="true" />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 relative">
        <div className="overline-label mb-5">Workshops & Automation</div>
        <h1 className="font-display font-bold tracking-tighter leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-white uppercase">
          Bring AI to your team.<br />
          <span className="font-bn-display italic font-normal normal-case text-gradient-glow">We build the system.</span>
        </h1>
        <p className="text-slate-200 mt-6 max-w-2xl leading-relaxed">
          Your team or university already has the talent and the tools — what's missing is a system for using AI the right way. We build that: hands-on workshops and custom automation designed around your operations.
        </p>

        <div className="mt-16 grid md:grid-cols-3 gap-5" style={{ perspective: "1200px" }}>
          {items.map((it, i) => (
            <TiltCard key={it.title} className="h-full">
              <div className="card-void p-9 flex flex-col h-full" data-testid={`workshop-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-6">
                  <it.icon className="text-orange-400" size={22} strokeWidth={1.6} aria-hidden="true" />
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-white">{it.title}</h3>
                <div className="font-bn-display text-base text-slate-200 mt-1 italic">{it.bn}</div>
                <p className="text-slate-200 text-sm mt-4 leading-relaxed flex-1">{it.desc}</p>
                <a href="mailto:hello@ailearninbangla.com" className="mt-8 inline-flex items-center gap-2 text-orange-400 text-xs font-semibold uppercase tracking-[0.15em] hover:gap-3 transition-all">
                  {it.cta} <ArrowUpRight size={14} />
                </a>
              </div>
            </TiltCard>
          ))}
        </div>

        <div className="mt-20 relative glass-panel rounded-[28px] overflow-hidden p-10 md:p-14">
          <div className="orb orb-magenta w-[24rem] h-[24rem] -right-32 -bottom-32" aria-hidden="true" />
          <div className="absolute inset-0 dot-grid opacity-30" aria-hidden="true" />
          <div className="relative grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] text-white">
                Ready to upgrade<br /> your team's <span className="text-gradient">output?</span>
              </h2>
              <p className="text-slate-200 mt-5 max-w-xl font-bn">The first discovery call with your team takes 15 minutes. Tell us your goal — we'll show you what's possible.</p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <a href="mailto:hello@ailearninbangla.com" className="btn-glow" data-testid="workshops-email-cta">
                Email Us <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
